import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Layers,
  Send,
  UserCheck,
  Loader2,
  Inbox,
  Bell,
  Mail,
  Phone,
  FileText,
  Palette,
  Sparkles,
  Search,
  ArrowLeft,
  Trash2,
  CheckCircle2,
  Circle,
  Clock,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { plannedActivitiesApi } from "../lib/plannedActivitiesApi";
import useTenantWebSocket from "../hooks/useTenantWebSocket";
import PostponeButton from "../components/construction/PostponeButton";
import { useAuthStore } from "../store/authStore";
import AssignedByBadge from "../components/users/AssignedByBadge";

const SCOPES = [
  { key: "upcoming", label: "Yaklaşan", Icon: CalendarIcon, accent: "emerald" },
  { key: "assigned_to_me", label: "Bana Atananlar", Icon: UserCheck, accent: "amber" },
  { key: "all", label: "Tümü", Icon: Layers, accent: "violet" },
  { key: "delegated", label: "Delege Ettiklerim", Icon: Send, accent: "sky" },
];

const TYPE_META = {
  email:  { label: "E-posta",  Icon: Mail,     tone: "bg-blue-50 text-blue-700 border-blue-200" },
  phone:  { label: "Telefon",  Icon: Phone,    tone: "bg-sky-50 text-sky-700 border-sky-200" },
  quote:  { label: "Teklif",   Icon: FileText, tone: "bg-amber-50 text-amber-700 border-amber-200" },
  design: { label: "Tasarım",  Icon: Palette,  tone: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
  custom: { label: "Özel",     Icon: Sparkles, tone: "bg-zinc-50 text-zinc-700 border-zinc-200" },
};

const PRIORITY_META = {
  high:   { label: "Yüksek", tone: "bg-red-100 text-red-700" },
  normal: { label: "Normal", tone: "bg-blue-100 text-blue-700" },
  low:    { label: "Düşük",  tone: "bg-zinc-100 text-zinc-600" },
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * `/app/my-activities` — full-page view of the current user's planned
 * activities. Extends the Dashboard widget with per-scope tabs, search,
 * type filter, "tamamlananları göster" toggle, and inline complete/
 * delete actions. Realtime via `planned_activity_changed` WS events.
 */
export default function MyActivitiesPage() {
  const [params, setParams] = useSearchParams();
  const urlScope = params.get("scope");
  const scope = urlScope || "upcoming";
  const currentUser = useAuthStore((s) => s.user);
  const currentUserId = currentUser?.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // Smart-default: on first mount with no ?scope= in the URL, pick the
  // first non-empty scope from [upcoming, delegated] so a user with only
  // delegated work doesn't land on an empty "Yaklaşan" page. Users who
  // arrive via a Dashboard widget always pass ?scope=… explicitly so
  // this only kicks in for direct sidebar clicks.
  useEffect(() => {
    if (urlScope) return; // user chose explicitly
    let cancelled = false;
    plannedActivitiesApi
      .mineCounts()
      .then((counts) => {
        if (cancelled) return;
        // Priority order: upcoming (has my active work) → assigned_to_me
        // (someone else delegated to me) → delegated (I handed off).
        // If everything is zero, stay on the default empty "Yaklaşan".
        if (counts.upcoming > 0) return;
        if (counts.assigned_to_me > 0) {
          setParams({ scope: "assigned_to_me" }, { replace: true });
        } else if (counts.delegated > 0) {
          setParams({ scope: "delegated" }, { replace: true });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [urlScope, setParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await plannedActivitiesApi.mine({
        scope,
        include_completed: showCompleted,
        limit: 100,
      });
      setItems(list);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [scope, showCompleted]);

  useEffect(() => {
    load();
  }, [load]);

  useTenantWebSocket((msg) => {
    if (msg?.event === "planned_activity_changed") load();
  });

  const filtered = useMemo(() => {
    let out = items;
    if (typeFilter) out = out.filter((a) => a.activity_type === typeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(
        (a) =>
          (a.title || "").toLowerCase().includes(q) ||
          (a.description || "").toLowerCase().includes(q) ||
          (a.company_name || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [items, typeFilter, search]);

  const setScope = (nextScope) => {
    setParams({ scope: nextScope });
  };

  const toggleCompleted = async (a) => {
    try {
      await plannedActivitiesApi.update(a.id, { completed: !a.completed });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncellenemedi");
    }
  };

  const removeActivity = async (a) => {
    if (!window.confirm(`"${a.title || "Bu aktivite"}" silinsin mi?`)) return;
    try {
      await plannedActivitiesApi.remove(a.id);
      toast.success("Silindi");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="p-6" data-testid="my-activities-page">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <Link
            to="/app/dashboard"
            className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900"
          >
            <ArrowLeft className="h-3 w-3" /> Dashboard
          </Link>
          <h1 className="mt-1 font-display text-2xl font-semibold text-zinc-900">
            Aktivitelerim
          </h1>
          <p className="text-xs text-zinc-500">
            Sana atanmış veya senin delege ettiğin planlı aktiviteler.
          </p>
        </div>
      </div>

      {/* Scope tabs */}
      <div className="mb-4 flex flex-wrap gap-1.5">
        {SCOPES.map((s) => {
          const isActive = scope === s.key;
          const toneMap = {
            emerald: isActive ? "bg-emerald-600 text-white shadow" : "text-emerald-700 hover:bg-emerald-50",
            violet:  isActive ? "bg-violet-600 text-white shadow" : "text-violet-700 hover:bg-violet-50",
            sky:     isActive ? "bg-sky-600 text-white shadow" : "text-sky-700 hover:bg-sky-50",
            amber:   isActive ? "bg-amber-600 text-white shadow" : "text-amber-700 hover:bg-amber-50",
          };
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setScope(s.key)}
              data-testid={`scope-tab-${s.key}`}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors ${
                isActive ? "border-transparent" : "border-zinc-200 bg-white"
              } ${toneMap[s.accent]}`}
            >
              <s.Icon className="h-3.5 w-3.5" />
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ara..."
            data-testid="my-activities-search"
            className="h-8 w-56 pl-7 text-xs"
          />
        </div>
        <Select
          value={typeFilter || "__all__"}
          onValueChange={(v) => setTypeFilter(v === "__all__" ? "" : v)}
        >
          <SelectTrigger className="h-8 w-36 text-xs" data-testid="my-activities-type-filter">
            <SelectValue placeholder="Tümü" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tümü</SelectItem>
            {Object.entries(TYPE_META).map(([k, m]) => (
              <SelectItem key={k} value={k}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <Checkbox
            id="my-show-completed"
            checked={showCompleted}
            onCheckedChange={setShowCompleted}
            data-testid="my-activities-show-completed"
          />
          <label htmlFor="my-show-completed" className="cursor-pointer text-xs text-zinc-600">
            Tamamlananları göster
          </label>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-200 bg-zinc-50/40 p-16 text-center">
          <Inbox className="h-10 w-10 text-zinc-400" />
          <div className="text-sm font-medium text-zinc-700">Aktivite bulunamadı</div>
          <div className="text-xs text-zinc-500">Filtreleri değiştirmeyi deneyebilirsin.</div>
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 bg-white">
          {filtered.map((a) => (
            <Row
              key={a.id}
              activity={a}
              scope={scope}
              currentUserId={currentUserId}
              onToggleCompleted={() => toggleCompleted(a)}
              onDelete={() => removeActivity(a)}
              onChanged={load}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({ activity: a, scope, currentUserId, onToggleCompleted, onDelete, onChanged }) {
  const meta = TYPE_META[a.activity_type] || TYPE_META.custom;
  const navigate = useNavigate();
  const isMuted = a.completed;
  const canDelete = scope !== "upcoming" || !a.completed; // basic guard, deletion always allowed by backend for owner
  const isOverdue =
    !a.completed &&
    a.scheduled_at &&
    new Date(a.scheduled_at) < new Date();
  // Only surface "X tarafından atandı" when the creator isn't me — a
  // to-do I made for myself doesn't need a self-attribution.
  const attributedTo =
    a.created_by && a.created_by !== currentUserId ? a.created_by_name : null;

  const goToConstruction = (e) => {
    // Same "don't hijack the row click when the user clicks the
    // AssignedByBadge / delete button" trick used by MyPlannedActivitiesCard.
    if (e.target.closest("[data-row-noop]")) return;
    navigate(`/app/cop/construction-tracking/${a.construction_id}`);
  };

  return (
    <li
      className={`flex items-start gap-3 px-4 py-3 ${isMuted ? "opacity-60" : ""}`}
      data-testid={`my-activity-row-${a.id}`}
    >
      <button
        type="button"
        onClick={onToggleCompleted}
        className="mt-1 shrink-0 text-zinc-400 hover:text-emerald-600"
        data-testid={`my-activity-toggle-${a.id}`}
      >
        {a.completed ? (
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
        ) : (
          <Circle className="h-5 w-5" />
        )}
      </button>

      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${meta.tone}`}>
        <meta.Icon className="h-4 w-4" />
      </span>

      <div
        role="button"
        tabIndex={0}
        onClick={goToConstruction}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(`/app/cop/construction-tracking/${a.construction_id}`);
          }
        }}
        className="min-w-0 flex-1 cursor-pointer rounded-md hover:underline focus:outline-none focus:ring-2 focus:ring-violet-200"
      >
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`text-sm font-medium ${isMuted ? "line-through text-zinc-500" : "text-zinc-900"}`}>
            {a.title || "Aktivite"}
          </span>
          {isOverdue && (
            <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">
              Gecikmiş
            </span>
          )}
          {a.priority && a.priority !== "normal" && (
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${PRIORITY_META[a.priority]?.tone || ""}`}>
              {PRIORITY_META[a.priority]?.label}
            </span>
          )}
          {a.reminder_enabled && !a.completed && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
              <Bell className="h-2.5 w-2.5" />
              Hatırlatma
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
          <span>{fmt(a.scheduled_at)}</span>
          {a.company_name && <span>· {a.company_name}</span>}
          {a.stand_no && <span>· Stant #{a.stand_no}</span>}
          {attributedTo && (
            <span data-row-noop>
              <AssignedByBadge
                userId={a.created_by}
                userName={attributedTo}
                userEmail={a.created_by_email}
                size="md"
              />
            </span>
          )}
        </div>
        {a.description && (
          <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-xs text-zinc-600">
            {a.description}
          </p>
        )}
      </div>

      {isOverdue && (
        <div className="shrink-0">
          <PostponeButton activity={a} onDone={onChanged} />
        </div>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="shrink-0 text-zinc-400 hover:text-red-600"
          title="Sil"
          data-testid={`my-activity-delete-${a.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </li>
  );
}
